import { Form } from "antd";
import type { FC } from "react";
import { useEffect, useState } from "react";

import type { TerminalProfileEntity } from "src/app/features/TerminalProfile/types/TerminalProfileEntity";
import { getAssignedDataGroup } from "../../../../core/router/auth";
import { ResourceName } from "../../../../core/router/config/ResourceName";
import type { Entity } from "../../../../core/types/fe";
import type { ExtendedColumnsType } from "../../../index";
import { PropertyTable } from "../../../Table/components/PropertyTable";
import { useLoadGenericFormQuery } from "../../services/server/formComponentsApi";
import { useLoadFormDefinitionQuery } from "../../services/server/serverApi";
import type { AntCustomFormField, FormField, QueryParams, SelectDynamicFormField } from "../../types/types";
import { FormComponent } from "../../types/types";
import { TerminalParameterSelectDynamicValue } from "./TerminalParameterSelectDynamicValue";

interface ParameterItem extends Entity {
    name: string;
    value: string;
    fieldConfig?: FormField;
}

export interface ParametersTableProps extends AntCustomFormField<number> {}

const propertiesToShow = [
    "terminalConfiguration.maxDegradedSfqueueSize",
    "terminalConfiguration.maxDegradedSfqueueAge",
    "terminalConfiguration.manualMiniBatchUpload",
    "transactionEntering",
    "defaultEmvKernelId",
    "emvTerminalType",
    "terminalCapabilities",
    "addTerminalCapabilities",
    "insertTimeout",
    "insertTimeoutCless",
    "maxIccTries",
    "minMsrTries",
    "maxMsrTries",
    "merchantCategoryCode",
    "pinEntryTimeout",
    "matAllowedCVMList",
    "languageList",
    "terminalCurrencyCode",
    "maxLogSize",
    "maxJournalAge",
    "integratedPrinting",
    "colorScheme",
    "financialTransactions",
    "administrationTransactions",
    "offlineMode",
    "offlineTransactions",
    "offlineMaxCount",
    "additionalSound",
    "displayTimeMillis",
    "confirmationTimeMillis",
    "displayResultTimeMillis",
    "partialApprovalConfirmOnScreen",
    "stanStep",
    "stanMin",
    "stanMax",
    "deferredModePrompt",
    "cardPresentRelatedTransactions",
    "rklConnectionString",
    "rklServerCommonName",
    "receiptTemplate",
    "forceSignatureOnTerminal",
    "merchantPreferenceOverride",
    "declinedHostMessageEnabled",
    "rklMode",
    "electronicReceiptDeliveryTimeout",
    "receiptDeliveryMethod",
];

export const TerminalsParametersTable: FC<ParametersTableProps> = () => {
    const [processedData, setProcessedData] = useState<ParameterItem[]>([]);

    const form = Form.useFormInstance();
    const dataGroup = getAssignedDataGroup();

    const terminalProfileId = form?.getFieldValue?.("terminalProfileId");

    const queryParams: QueryParams = {
        resource: ResourceName.TERMINAL_PROFILES,
        dataGroupId: dataGroup?.code,
        entityId: terminalProfileId ?? undefined,
    };

    const { data, isFetching } = useLoadGenericFormQuery(queryParams, { skip: !queryParams.entityId });

    const { data: formDefinition } = useLoadFormDefinitionQuery("terminal-profile");
    const relevantFields = formDefinition?.formConfiguration?.tabs[1].fields;

    useEffect(() => {
        const inputData: TerminalProfileEntity = { ...data };
        let processedInputData: ParameterItem[] = [];

        // Filter propertiesToShow by fields relevant to this DG - from TP.parameters tab formConfig
        const relevantFieldNames = relevantFields?.map((field) => field.name) ?? [];
        const filteredPropertiesToShow = propertiesToShow.filter((prop) => relevantFieldNames.includes(prop));

        filteredPropertiesToShow.forEach((prop) => {
            // prop may be a dotted path (e.g. "terminalConfiguration.maxDegradedSfqueueSize") into a nested object
            let inputVal: unknown = prop.split(".").reduce<unknown>((value, key) => (value as Record<string, unknown>)?.[key], inputData);
            if (typeof inputVal === "boolean") {
                inputVal = inputVal ? "true" : "false";
            } else if (Array.isArray(inputVal)) {
                inputVal = inputVal.join(", ");
            }
            const item: ParameterItem = {
                id: prop,
                name: prop,
                value: inputVal,
                fieldConfig: relevantFields?.find((field) => field.name === prop),
            } as ParameterItem;

            processedInputData = [...processedInputData, item];
        });

        setProcessedData(processedInputData);
    }, [data, relevantFields, setProcessedData]);

    const getColumnsConfig = (t: Function): ExtendedColumnsType<ParameterItem>[] => {
        return [
            {
                title: t("list.form.name"),
                key: "name",
                dataIndex: "name",
                render: (_, record) => t(`terminalProfileCreateForm.${record.name}`),
            },
            {
                title: t("list.form.value"),
                key: "value",
                dataIndex: "value",
                render: (_, record) => {
                    const isSelectDynamic =
                        record.fieldConfig?.component === FormComponent.SELECT_DYNAMIC ||
                        record.fieldConfig?.component === FormComponent.MULTI_SELECT_DYNAMIC;
                    if (isSelectDynamic) {
                        return (
                            <TerminalParameterSelectDynamicValue
                                fieldConfig={record.fieldConfig as SelectDynamicFormField}
                                value={record.value}
                            />
                        );
                    }
                    return record.value;
                },
            },
        ];
    };

    return (
        (queryParams.entityId && (
            <PropertyTable<ParameterItem>
                name="terminalParameters_table"
                rowsAreSelectable={false}
                getColumnsConfig={getColumnsConfig}
                propertyAsRowKey="id"
                isFetching={isFetching}
                tableData={processedData}
                readOnly={false}
                pagination={false}
            />
        )) ||
        null
    );
};
